import { Component, computed, DestroyRef, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from 'src/app/shared/services';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { MenubarModule } from 'primeng/menubar';
import { MenuItem } from 'primeng/api';
import { ImageModule } from 'primeng/image';
import { Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { LauncherModel, UserViewModel } from 'src/app/shared/models';
import { TooltipModule } from 'primeng/tooltip';
import { AvatarModule } from 'primeng/avatar';
import { PopoverModule, Popover } from 'primeng/popover';
import { SidebarModule } from 'primeng/sidebar';
import { OverlayPanelModule } from 'primeng/overlaypanel';

import { MenuModule, Menu } from 'primeng/menu';
import { BadgeModule } from 'primeng/badge';
import { A11yModule } from '@angular/cdk/a11y';

import { isAdmin } from '../utils/utils';
import { Observable } from 'rxjs';

// NEW
import { ContextStateService } from '../../shared/services';
import { Icons, LEGAL_LINKS } from 'src/app/shared/constants';
import { AuthService, ScreenService } from 'grow-angular/services';
import { DEFAULT_AVATAR } from '../../../magic';
import { AppContext } from 'src/app/shared/contracts';
import { ContextsQuery } from 'src/app/members.api.g';
import { AdminNavigationStateService } from 'src/app/pages-frontend/services/admin-navigation-state.service';
import packageJson from '../../../../package.json';

import { addClass, openUrl, removeClass } from '../utils/utils';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

import { CustomerLogoService } from 'src/app/pages-frontend/services/customer-logo.service';
import { NotificationsService } from 'src/app/pages-frontend/services/notifications.service';
import { SxMessageService } from 'src/app/pages-frontend/services/sx-message.service';
import { DataErrorType } from 'src/app/pages-frontend/services/data.service';

import {
  NotificationsComponent,
  NotificationItem,
  SetNotificationReadEvent,
} from '../../shared-frontend/shared-frontend.module';

type Context = ContextsQuery['context']['onlyWithLaunchers']['nodes'][0];

/**
 * Erkennt eine Bild-URL in der Icon-Angabe eines Launchers.
 *
 * Nötig, weil das Datenmodell keinen Typ mitliefert: `cssClasses` und `image`
 * sind beides nackte Strings (`Launcher.image: String`) und enthalten je nach
 * Konfiguration eine Icon-Font-Klasse ODER eine Bild-URL. Erkannt werden ein
 * URL-Anfang (http(s), absolut, relativ, data:image) und bekannte Bild-Endungen
 * -- Letzteres deckt auch pfadlose Angaben wie `assets/icons/chat.svg` ab.
 *
 * Modulebene statt `static #`: bei aktivem `experimentalDecorators` verbietet
 * TypeScript statische private Identifier in einer dekorierten Klasse (TS18036).
 */
const IMAGE_ICON_PATTERN = /^(https?:\/\/|\/|\.{1,2}\/|data:image\/)|\.(svg|png|jpe?g|webp|gif|ico)(\?|#|$)/i;

// Mindestabstand des Popover-Pfeils zu den Panel-Ecken (= PrimeNG-Default
// popover.arrow.offset, 1.25rem). Haelt den Pfeil aus dem Border-Radius heraus.
const POPOVER_ARROW_INSET_PX = 20;

@Component({
  selector: 'new-app-navigation-menu',
  standalone: true,
  imports: [
    CommonModule, 
    ButtonModule, 
    MenubarModule, 
    ImageModule, 
    TooltipModule, 
    AvatarModule, 
    PopoverModule,
    SidebarModule,
    OverlayPanelModule,
    MenuModule,
    BadgeModule,
    TranslateModule,
    A11yModule,
    NotificationsComponent,
  ],
  templateUrl: './navigation-menu.component.html',
  styleUrl: './navigation-menu.component.scss',
  host: {
    // Eingabe-Modus global erkennen (auch beim Hineintabben von außen ins Menü).
    '(document:keydown)': 'onDocKeydown()',
    '(document:pointerdown)': 'onDocPointerDown()',
    '[class.nav-input-keyboard]': 'keyboardMode()',
    // Logo im mobilen Header aus-/einblenden (Steuerung über showLogoOnMobile).
    '[class.nav-logo-hidden-mobile]': '!showLogoOnMobile',
  },
})
export class NavigationMenuComponent implements OnInit, OnDestroy {

  protected readonly debug: boolean = false;

  // Sichtbarer Menubar-Fokus nur bei Tastatur: PrimeNGs .p-focus bleibt nach einem
  // Maus-Klick kleben und :focus-visible matcht PrimeNGs programmatischen Fokus
  // nicht. Daher erkennen wir den Eingabe-Modus selbst (document-weit) und zeigen
  // den Fokus-Rahmen nur im Keyboard-Modus (WCAG 2.4.7). Gebunden als Host-Klasse.
  #keyboardMode = signal(false);
  protected readonly keyboardMode = this.#keyboardMode.asReadonly();

  // NEW
  private readonly screenService = inject(ScreenService);

  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly contextState = inject(ContextStateService);

  private readonly translate = inject(TranslateService);

  readonly #notificationsService = inject(NotificationsService);
  readonly #sxMessage = inject(SxMessageService);
  readonly #destroyRef = inject(DestroyRef);

  // Kundenlogo im Header. Uebernommen aus der DashboardComponent (dort stillgelegt):
  // das Logo gehoert in den Header, nicht auf eine einzelne Seite – so ist es auf
  // jeder Route sichtbar und wird nur einmal geladen/gepollt.
  readonly #customerLogoService = inject(CustomerLogoService);

  /** Object-URL (blob:) des Kundenlogos; leer, wenn keines konfiguriert ist. */
  readonly customerLogo = this.#customerLogoService.customerLogo;

  /** Mandantenname (units.defaultUnit.name) – dient als alt-Text des Logos. */
  readonly customerName = this.#customerLogoService.customerName;

  /**
   * Steuert, ob das Kundenlogo gerendert und geladen wird: nur ab Desktop-Breite
   * (> 1024px, ScreenService TABLET_MAX_WIDTH). Bis einschliesslich Tablet fehlt im
   * Header der Platz – dort wird es weder gerendert noch geladen (das Bild kommt
   * als Blob ueber den ImageReader, das waere sonst reine Bandbreitenverschwendung).
   */
  readonly #showCustomerLogo = signal(false);
  protected readonly showCustomerLogo = this.#showCustomerLogo.asReadonly();

  /**
   * Ob das Kundenlogo-Polling gerade laeuft. isDesktop$ feuert bei JEDEM (debounced)
   * Resize, nicht nur beim Wechsel des Breakpoints – ohne diesen Merker wuerde das
   * Polling bei jedem Resize-Tick neu gestartet.
   */
  #customerLogoPolling = false;

  protected userInfo: MenuItem[] = [];
  protected userName: string = '';
  private userEMail: string = '';
  isMobile$: Observable<boolean> = this.screenService.isMobile$;
  userAvatar = DEFAULT_AVATAR;

  dropDownOptions;
  
  icons = Icons;

  contexts: Context[] = [];
  currentContext: AppContext | undefined;

  // Rohdaten (Signal) aus dem NotificationsService.
  readonly #notifications = this.#notificationsService.notifications;

  // Abgeleitetes View-Model für das Notifications-Popover (News -> NotificationItem).
  readonly notifications = computed<NotificationItem[]>(() =>
    this.#notifications().map((n) => ({
      type: 'news',
      author: {
        firstName: n.author?.firstName ?? '',
        lastName: n.author?.lastName ?? '',
        imageBase64: n.author?.imageBase64 ?? '',
      },
      // publishedDate ist optional -> creationDate (pflicht) als Fallback.
      publishedDate: new Date(n.publishedDate ?? n.creationDate),
      title: { value: n.title?.value ?? '' },
      message: { value: n.message?.value ?? '' },
      isImportant: n.isImportant ?? false,
      isRead: n.isRead ?? false,
      uuid: n.uuid,
      userNotificationUuid: n.userNewsUuid ?? '',
    }))
  );

  // Anzahl der ungelesenen Notifications (nur für die "unread"-Anzeige).
  readonly unreadNotificationsCount = computed<number>(
    () => this.#notifications().filter((n) => !n.isRead).length,
  );

  // Fehler-Signal aus dem NotificationsService (DataError | null).
  readonly notificationsError = this.#notificationsService.error;

  // Entprellung: pro Fehler-Serie nur EIN Toast. load() setzt zu Beginn jedes
  // Poll-Zyklus error=null, daher wird nicht über error===null zurückgesetzt,
  // sondern über lastUpdated (= ein echter erfolgreicher Load dazwischen).
  #errorToastShown = false;
  #lastOkShown: number | null = null;
  sub$: Subscription[] = [];
  tertiaryMenuItems: any;

  // KS, 20260517
  private adminNavigationStateService = inject(AdminNavigationStateService);

  // NEW EMD

  items?: MenuItem[] | undefined;

  // Basis-Menü (Sekundär-Navigation aus dem Kontext) OHNE die mobil ergänzten
  // Rechtstext-Links. #composeMenu() setzt daraus this.items zusammen.
  #secondaryItems: MenuItem[] = [];

  logoImg: String = '../../../assets/pictures/redesign-logo.svg';

  /**
   * Steuert die Logo-Sichtbarkeit im mobilen Header (Burger-Modus, <= 960px).
   *
   * false (Default): Logo ist mobil ausgeblendet, der Burger rückt nach links.
   * true: Logo wird auch mobil wieder eingeblendet.
   *
   * Zum Wiedereinblenden genügt es, diesen Wert auf true zu setzen – die
   * konkrete Positionierung (Logo aus / Burger links) steuert die Host-Klasse
   * `nav-logo-hidden-mobile` im SCSS.
   */
  protected readonly showLogoOnMobile = false;

  userData: any | undefined;

  //roles: Observable<Array<{ id: number; name: string; }>> | undefined;
  roles: Array<{ id: number; name: string; }> = [];

  //sub$: Subscription[] = [];

  constructor(
    //private userService: UserService,
    //private router: Router,
  ) {

    // NEW ???
    this.dropDownOptions = {
      shading: false,
      width: '300px',
      position: { my: { x: 'right', y: 'top' }, at: { x: 'right', y: 'top' }, offset: { y: '59' } },
    };

    // Zeigt bei einem Lade-/Netzwerk-/Timeout-Fehler der Notifications einen Toast an.
    // Entprellt über lastUpdated: pro Fehler-Serie nur EIN Toast (load() setzt zu
    // Beginn jedes Poll-Zyklus error=null, daher nicht über error===null zurücksetzen).
    effect(() => {
      const err = this.notificationsError();
      const lastOk = this.#notificationsService.lastUpdated();

      // Nach einem erfolgreichen (Re-)Load die Sperre lösen, damit ein
      // künftiger Fehler wieder gemeldet wird.
      if (lastOk !== null && lastOk !== this.#lastOkShown) {
        this.#lastOkShown = lastOk;
        this.#errorToastShown = false;
      }

      if (err && !this.#errorToastShown && this.#notificationsService.showErrorMessages) {
        this.#errorToastShown = true;
        const detailKey =
          err.type === DataErrorType.TIMEOUT_ERROR
            ? 'redesign.notifications.TIMEOUT_ERROR'
            : 'redesign.notifications.LOAD_ERROR';
        this.#sxMessage.error(
          this.translate.instant(detailKey),
          this.translate.instant('redesign.toast.ERROR'),
        );
      }
    });

  }

  // NEW
  getAngularVersion(): string {
    console.log(packageJson.dependencies['@angular/core']);
    return packageJson.dependencies['@angular/core'];
  }

  // NEW
  getDevExtremeVersion(): string {
    return packageJson.dependencies['devextreme'];
  }

  getPrimeNGVersion(): string {
    return packageJson.dependencies['primeng'];
  }

  
  // Fokus beim Öffnen in den (an <body> angehängten) Popover holen: sonst ist der
  // Inhalt per Tastatur unerreichbar, weil das Panel am DOM-Ende liegt und Tab vom
  // Trigger zum nächsten Header-Control statt in den Popover springt.
  onPopoverShow(popover: Popover, trigger?: HTMLElement): void {
    this.#pinOverlayToViewport(popover.container as HTMLElement | null | undefined);
    this.#alignPopoverArrow(popover, trigger);

    // cdkTrapFocus fügt am Anfang/Ende je einen fokussierbaren Anchor
    // (.cdk-focus-trap-anchor, tabindex=0) ein. Der Start-Anchor MUSS
    // ausgeschlossen werden – ihn zu fokussieren würde den Fokus ans Ende
    // (letztes Item) umleiten. Daher: erstes echtes fokussierbares Element.
    const first = popover.container?.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not(.cdk-focus-trap-anchor)',
    );
    first?.focus();
  }

  // Das User-Menue hat dasselbe Problem wie das Notifications-Popover
  // (s. #pinOverlayToViewport): Es haengt an <body> und wird in DOKUMENT-
  // Koordinaten platziert, waehrend der Header position:fixed ist. PrimeNGs
  // eigener Scroll-Schutz greift hier nicht – der ConnectedOverlayScrollHandler
  // beobachtet nur Vorfahren mit overflow:auto|scroll; window/document ist
  // ausgeschlossen (DomHandler.getScrollableParents), das Menue bleibt beim
  // Window-Scroll also offen und wandert mit.
  //
  // queueMicrotask ist noetig: Menu emittiert onShow VOR appendOverlay() und
  // alignOverlay() (anders als Popover, das erst danach emittiert). Direkt
  // gesetzte top/left wuerden von alignOverlay() wieder ueberschrieben. Der
  // Microtask laeuft nach dem synchronen Animations-Handler, aber noch vor dem
  // Paint – also sprungfrei.
  onUserMenuShow(menu: Menu): void {
    const panel = menu.container;
    queueMicrotask(() => this.#pinOverlayToViewport(panel));
  }

  // Das Panel haengt an <body> und wird von PrimeNG mit position:absolute in
  // DOKUMENT-Koordinaten platziert (align()/alignOverlay() -> absolutePosition,
  // top/left enthalten den Window-Scroll). Der Header ist position:fixed und
  // scrollt nicht mit – beim Scrollen wandert das Panel deshalb weg. Die
  // Umrechnung auf VIEWPORT-Koordinaten (position:fixed) haelt den Abstand zum
  // Trigger konstant. Sprungfrei, weil beide Koordinatensysteme beim aktuellen
  // Scroll-Stand dieselbe Position ergeben. Wird von PrimeNG nicht
  // ueberschrieben: das Ausrichten laeuft genau einmal beim Oeffnen, und bei
  // Resize schliessen beide Overlays ohnehin (onWindowResize -> hide()).
  #pinOverlayToViewport(panel: HTMLElement | null | undefined): void {
    if (!panel) return;

    const top = parseFloat(panel.style.top);
    const left = parseFloat(panel.style.left);
    if (!Number.isFinite(top) || !Number.isFinite(left)) return;

    panel.style.position = 'fixed';
    panel.style.top = `${top - window.scrollY}px`;
    panel.style.left = `${left - window.scrollX}px`;
  }

  // PrimeNG setzt in align() zwar --overlayArrowLeft, liest die Variable im
  // Popover-Stylesheet aber nirgends aus: dort haengt der Pfeil fest auf
  // left: 1.25rem (Token popover.arrow.offset) und zeigt damit nicht auf den
  // Trigger. Den echten Offset hier berechnen und dem Stylesheet als
  // --sx-popover-arrow-left uebergeben (verbraucht in dieser Komponente).
  // Horizontal messbar trotz laufender Einblend-Animation: die betrifft nur
  // opacity und transform: scaleY() – die x-Geometrie bleibt unveraendert.
  #alignPopoverArrow(popover: Popover, trigger?: HTMLElement): void {
    const panel = popover.container as HTMLElement | null | undefined;
    if (!panel || !trigger) return;

    const panelRect = panel.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    if (panelRect.width === 0) return;

    // Mitte des Triggers, relativ zur linken Panel-Kante ...
    const center = triggerRect.left + triggerRect.width / 2 - panelRect.left;
    // ... aber nie in die abgerundeten Ecken hinein.
    const left = Math.min(
      Math.max(center, POPOVER_ARROW_INSET_PX),
      panelRect.width - POPOVER_ARROW_INSET_PX,
    );

    panel.style.setProperty('--sx-popover-arrow-left', `${left}px`);
  }

  // Fokus-Rückgabe an den Trigger beim Schließen (WCAG 2.4.3).
  // Deferred: Beim onHide ist der Fokus synchron noch auf dem (gerade entfernten)
  // Item; erst danach fällt er auf <body> zurück. Daher nach dem Ausblenden prüfen
  // und nur zurückgeben, wenn der Fokus "verloren" ging. Fokussiert der Nutzer
  // bewusst ein anderes Element (Outside-Click darauf), bleibt dieser erhalten.
  onPopoverHide(trigger?: HTMLElement): void {
    if (!trigger) return;
    setTimeout(() => {
      const active = document.activeElement;
      if (active === document.body || active === null) {
        trigger.focus();
      }
    });
  }

  // Eingabe-Modus-Erkennung für den sichtbaren Menubar-Fokus (s. #keyboardMode).
  protected onDocKeydown(): void {
    if (!this.#keyboardMode()) this.#keyboardMode.set(true);
  }

  protected onDocPointerDown(): void {
    if (this.#keyboardMode()) this.#keyboardMode.set(false);
  }

  

  ngOnInit() {

    let currentUser: UserViewModel;

    // NEW
    this.sub$.push(
      this.userService
        .getCurrentUser()
        .pipe(filter((user) => user.id !== null))
        .subscribe((user) => {

          if(this.debug) console.log('[NavigationMenuComponent] ngOnInit, user=', user);

          this.userName = user.firstName && user.lastName ? user.firstName + ' ' + user.lastName : user.name ?? '';
          this.userEMail = user.contactEmail ?? '';
          this.userAvatar = user.imageBase64 ? user.imageBase64 : DEFAULT_AVATAR;

          this.userInfo = [

            {
              label: this.userName,
              state: { 
                subLabel: this.userEMail,
              },
              items: [

                ...(isAdmin() && !this.#isPhone() ? [{
                    label: this.translate.instant('redesign.header.PROFILE.OPEN_ADMIN_NAV'),
                    ariaLabel: this.translate.instant('redesign.header.PROFILE.OPEN_ADMIN_NAV'),
                    icon: 'fa-regular fa-sidebar',
                    styleClass: 'sx-button-xs',
                    state: {
                        isButton: true,
                        // Marker, damit #applyAdminNavItemState() das Item wiederfindet.
                        adminNavToggle: true,
                    },
                    command: () => {

                      // Label/Style des Buttons aktualisiert das Abo auf
                      // adminNavigationState$ (siehe ngOnInit) – hier nur togglen.
                      this.toogleAdminMenu();
                      if(this.debug) console.log('[NavigationMenuComponent] adminNavigationState=', this.adminNavigationStateService.getState());

                      if (!this.adminNavigationStateService.getState()) {
                        // Beim Schließen des Admin-Menüs auf den ersten Menubar-Eintrag
                        // (i. d. R. Dashboard) wechseln.
                        this.#navigateToFirstMenuItem();
                      }

                    }
                }] : []),
                {
                    label: this.translate.instant('redesign.header.PROFILE.SETTINGS'),
                    ariaLabel: this.translate.instant('redesign.header.PROFILE.SETTINGS'),
                    icon: 'fa-regular fa-gear',
                    //routerLink: '/profile',
                    styleClass: 'sx-button-xs',
                    state: { 
                        isButton: true,
                    },
                    command: () => {
                        this.router.navigate(['/profile']);
                    }
                },
                {
                    label: this.translate.instant('redesign.header.PROFILE.LOGOUT'),
                    ariaLabel: this.translate.instant('redesign.header.PROFILE.LOGOUT'),
                    icon: 'fa-sharp fa-regular fa-right-from-bracket',
                    styleClass: 'sx-button-xs outline',
                    state: { 
                        isButton: true,
                    },
                    command: () => {
                        this.logOut();
                    }
                }

                ]
            }

          ];

          // userInfo wird erst hier (asynchron) aufgebaut – daher den Toggle-Button
          // einmalig auf den bereits bestehenden Drawer-Zustand ausrichten.
          this.#applyAdminNavItemState();

        }),

      this.contextState.contexts.subscribe((contexts) => {
        this.contexts = contexts;
      }),

      this.contextState.currentContext.subscribe((context) => {
        this.currentContext = context;
        this.tertiaryMenuItems = context.menuItems.tertiary;

        // KS, 20260204
        if(this.debug) console.info('[NavigationMenuComponent] ngOnInit, currentContext=',this.currentContext);

        /*
        console.info('[NavigationMenuComponent] ngOnInit, currentContext=',this.currentContext);
        if(context.image) {
          this.logoImg = context.image;
        }
          */
      })

    );

    /* OLD
    this.sub$.push(
          this.userService
            .getCurrentUser()
            .pipe(filter((user) => user.id !== null))
            .subscribe((user) => {

              console.warn('++++++++++ user=', user);
              console.warn('++++++++++ user.roles=', user.roles);
 
            })
        );
        */
    
    this.sub$.push(
        this.userService.getCurrentUser().subscribe(user => {
        currentUser = user;
        if(this.debug) console.log('[NavigationMenuComponent] ngOnInit, current user:', currentUser);
        this.userData = currentUser;
      })
    );

    // das ist 
    this.sub$.push(
      this.userService.getUserRoles().subscribe((roles) => {
        //console.log('roles=', roles);
        this.roles = roles;
        if(this.debug) console.warn('[NavigationMenuComponent] ngOnInit, roles=', this.roles);
      })
    );

     this.initializeSecondaryMenu();

     // Notifications-Polling im Service starten (erster Tick lädt sofort frisch aus
     // der DB). Das View-Model (this.notifications) und unreadNotificationsCount
     // reagieren reaktiv über das notifications-Signal des Service.
     this.#notificationsService.startPolling();

     // Phone-Breakpoint (<= 768px) als reaktiver Trigger: isPhone$ (debounced Resize
     // aus dem ScreenService, kein zusätzlicher window-Listener) stößt bei jeder
     // Änderung das Neuzusammenbauen an; die 768-Entscheidung selbst trifft
     // #composeMenu() live über #isPhone(). BehaviorSubject -> feuert sofort.
     this.sub$.push(
       this.screenService.isPhone$.subscribe(() => this.#composeMenu())
     );

     // Kundenlogo (Rendern, Laden, Polling) erst ab Desktop-Breite (> 1024px).
     // Bewusst NICHT isTablet$: das ist ein exklusives Band (961–1024px) und deckt
     // Phone/Mobile nicht mit ab. isDesktop$ ist die passende Gegenprobe und deckt
     // sich mit respond-to('tablet') im SCSS. BehaviorSubject -> feuert sofort.
     this.sub$.push(
       this.screenService.isDesktop$.subscribe((isDesktop) =>
         this.#syncCustomerLogoPolling(isDesktop)
       )
     );

     // Sprachwechsel: Menü neu zusammenbauen, damit die per instant() aufgelösten
     // Legal-Labels (Gruppe + Links) mit der aktiven Sprache mitwechseln.
     this.sub$.push(
       this.translate.onLangChange.subscribe(() => this.#composeMenu())
     );

     // Admin-Navigation: Label/Style des Toggle-Buttons folgt dem Service-State.
     // Als Abo (statt Push nach jedem Toggle), damit der Button auch stimmt, wenn
     // der Drawer routen-gesteuert im Service geschlossen wurde.
     this.sub$.push(
       this.adminNavigationStateService.adminNavigationState$.subscribe(
         () => this.#applyAdminNavItemState()
       )
     );

  }

  ngOnDestroy() {
    this.items = undefined;  // Menu-Items löschen
    this.userData = null;  // User-Daten löschen

    this.#notificationsService.stopPolling();
    this.#customerLogoService.stopPolling();

    this.sub$.forEach((item) => item.unsubscribe());
  }

  // NEW
  // User Icon/Avatar
  onItemClick(event: any) {
    if(this.debug) console.log('[NavigationMenuComponent] onItemClick, event=', event);
    event?.itemData?.click?.call();
  }

  // NEW
  logOut() {
    const iframe = document.querySelector('iframe.application-frame') as HTMLIFrameElement;
    if (iframe) {
      const origin = new URL(iframe.src).origin;
      const appWindow = iframe.contentWindow;
      appWindow?.postMessage({ type: 'log_out' }, origin);
    }
    this.authService.logOut();
  }

  toogleAdminMenu() {
    if(this.debug) console.log('[NavigationMenuComponent] toggleAdminMenu');
    this.adminNavigationStateService.toggleState();
  }

  // Bringt Label, ariaLabel und die "outline"-Klasse des Admin-Toggle-Buttons im
  // User-Menü auf den aktuellen Stand des AdminNavigationStateService. Zentral,
  // damit der Button auch dann stimmt, wenn das Admin-Menü NICHT über ihn selbst
  // geschlossen wurde (z. B. routen-gesteuertes Auto-Close im Service).
  #applyAdminNavItemState(): void {
    const item = this.userInfo
      .flatMap((group) => group.items ?? [])
      .find((candidate) => candidate.state?.['adminNavToggle'] === true);

    if (!item) return;

    const isOpen = this.adminNavigationStateService.getState();
    const labelKey = isOpen
      ? 'redesign.header.PROFILE.CLOSE_ADMIN_NAV'
      : 'redesign.header.PROFILE.OPEN_ADMIN_NAV';

    item.label = this.translate.instant(labelKey);
    item.ariaLabel = item.label;

    if (isOpen) {
      addClass(item, 'outline', 'styleClass');
    } else {
      removeClass(item, 'outline', 'styleClass');
    }

    // WICHTIG: Referenz erneuern für die PrimeNG Change Detection des p-menu
    // (dort hängt der Button, nicht in der Menubar).
    this.userInfo = [...this.userInfo];
  }

  // Navigiert auf den ersten Menubar-Eintrag (das Logo liegt im "start"-Template und
  // ist kein items-Eintrag). Auf dem Phone hängt am Ende die Legal-Gruppe ohne
  // routerLink -> daher der erste Eintrag MIT routerLink.
  #navigateToFirstMenuItem(): void {
    const routerLink = this.items?.find((item) => !!item.routerLink)?.routerLink;
    if (!routerLink) return;
    if(this.debug) console.log('[NavigationMenuComponent] navigateToFirstMenuItem, routerLink=', routerLink);
    this.router.navigate(Array.isArray(routerLink) ? routerLink : [routerLink]);
  }

  /**
   * Startet bzw. stoppt Laden und Polling des Kundenlogos passend zum Breakpoint.
   *
   * Bis einschliesslich Tablet (<= 1024px) wird das Logo nicht gerendert (siehe
   * showCustomerLogo im Template) – dann wird es auch nicht geladen. Beim Wechsel
   * zurueck auf Desktop-Breite wird einmal frisch geladen und das Polling wieder
   * aufgenommen.
   */
  #syncCustomerLogoPolling(isDesktop: boolean): void {
    this.#showCustomerLogo.set(isDesktop);

    if (!isDesktop) {
      if (!this.#customerLogoPolling) return;
      this.#customerLogoService.stopPolling();
      this.#customerLogoPolling = false;
      return;
    }

    if (this.#customerLogoPolling) return;
    this.#customerLogoService.loadCustomerLogo();
    // Erster Poll-Tick erst mit dem naechsten regulaeren Intervall – loadCustomerLogo()
    // hat gerade frisch geladen, sonst gaebe es zwei Requests direkt hintereinander.
    // 'interval' statt einer Zahl: das Intervall kennt nur der Service (pollInterval in
    // seiner DataService-Config), hier gaebe es sonst eine Kopie, die stillschweigend
    // veraltet.
    this.#customerLogoService.startPolling('interval');
    this.#customerLogoPolling = true;
  }

  // Phone-Erkennung (768px = 'phone'-Breakpoint der App, konsistent mit den CSS-
  // Media-Queries/respond-to). Bewusst NICHT ScreenService.isMobile$, das erst bei
  // 960px (Phone + Tablet) schaltet – hier ist eine reine Phone-Grenze gewollt.
  // Wird beim Aufbau des User-Menüs (User-Load) ausgewertet.
  #isPhone(): boolean {
    return window.innerWidth <= 768;
  }

  private initializeSecondaryMenu() {

    const currentUrl = this.router.url;
    if(this.debug) console.log('[NavigationMenuComponent] initializeSecondaryMenu, currentUrl=', currentUrl);

    if(this.debug) console.log('[NavigationMenuComponent] initializeSecondaryMenu, this.currentContext=', this.currentContext);

    if(this.debug) console.log('[NavigationMenuComponent] initializeSecondaryMenu ', this.currentContext?.menuItems.secondary);

    const secondary: LauncherModel[] = this.currentContext?.menuItems?.secondary ?? [];
    if(this.debug) console.log('[NavigationMenuComponent] initializeSecondaryMenu, secondary=', secondary);

    this.#secondaryItems = [];
    for (const menuItem of secondary) {
      if(this.debug) console.log(menuItem);
      this.#secondaryItems.push({
        label: menuItem?.text,
        routerLink: menuItem?.path,
      });
    }

    // Menubar-Modell zusammenbauen (mobil inkl. "Rechtliches"-Untergruppe).
    this.#composeMenu();

    /*
    if(!this.items) {

      this.items = [
              {
                  label: 'Dashboard X',
                  //escape: false, // falls html im label
                  routerLink: ['/v2/dashboard'],
                  //routerLink: '/dashboard',
                  //queryParams: [],
                  //url: 'external link',
                  //command: () => {},
                  //command: () => this.someFn(),
                  //routerLinkActiveOptions: {}
                  //disabled: currentUrl === '/dashboard',
                  routerLinkActiveOptions: { exact: true }
                },
                {
                  label: 'Learning Space X',
                  routerLink: ['/v2/learning-space'],
                  //routerLink: '/learning-space',
                  //disabled: currentUrl === '/learning-space',
                  //routerLinkActiveOptions: {}
                  routerLinkActiveOptions: { exact: true }
                },
                {
                  label: 'Catalogue X',
                  routerLink: ['/v2/catalogue'],
                  //routerLink: '/catalogue',
                  //disabled: currentUrl === '/catalogue',
                  //routerLinkActiveOptions: {}
                  routerLinkActiveOptions: { exact: true }
                }                
      ];

    }
      */
  }

  // Baut das Menubar-Modell zusammen: Sekundär-Navigation und – nur auf dem Phone
  // (<= 768px, siehe #isPhone()) – die "Rechtliches"-Untergruppe mit den
  // Footer-Rechtstext-Links. Neue Array-Referenz für die PrimeNG Change Detection.
  // Wird initial (initializeSecondaryMenu) und bei jeder isPhone$-Änderung aufgerufen.
  #composeMenu(): void {
    this.items = this.#isPhone()
      ? [...this.#secondaryItems, this.#buildLegalSubmenu()]
      : [...this.#secondaryItems];
  }

  // Untergruppe "Rechtliches" mit den externen Rechtstext-Links (Impressum/AGB/
  // Datenschutz) aus der geteilten LEGAL_LINKS-Quelle. Labels werden im Template
  // über state.i18nKey + translate-Pipe aufgelöst (reagiert auf Sprachwechsel).
  #buildLegalSubmenu(): MenuItem {
    return {
      // label als robuster Fallback: PrimeNG rendert die Gruppe/Kinder mit
      // Default-Markup, das nur item.label nutzt (state.i18nKey kommt dort nicht
      // an). Ohne label bleiben Gruppe und Kinder leer. state.i18nKey bleibt für
      // das Custom-Template erhalten. Reaktivität bei Sprachwechsel via
      // translate.onLangChange -> #composeMenu().
      label: this.translate.instant('redesign.header.MENU.LEGAL'),
      state: { i18nKey: 'redesign.header.MENU.LEGAL', group: true },
      items: LEGAL_LINKS.map((link) => ({
        label: this.translate.instant(link.labelKey),
        // PrimeNG rendert die Submenu-Links mit Default-Markup und stellt kein
        // rel-Feld bereit. Daher öffnen wir den Link selbst kontrolliert über
        // openUrl() (setzt noopener,noreferrer) statt über url/target.
        // AA-Umsetzung: kein "öffnet in neuem Fenster"-Hinweis (das wäre WCAG 3.2.5
        // Level AAA). Ein versteckter SR-Hinweis ist im p-menubar ohnehin nicht
        // möglich, weil das <li role="menuitem"> sein aria-label aus item.label
        // bezieht und Nachfahren-Text überschreibt (item.ariaLabel wird ignoriert).
        command: () => openUrl(link.url),
        state: { i18nKey: link.labelKey, external: true },
      })),
    };
  }

  onClickLogo() {
    this.router.navigate(['/']);
  }

  /**
   * Icon-Angabe eines Tertiary-Eintrags: `cssClasses`, sonst `image`.
   *
   * Der Fallback ist nötig, weil `cssClasses` in der Datenbank gepflegt wird und
   * fehlen kann (die API liefert dann ""). Ohne ihn bliebe der Button leer: eine
   * leere Icon-Angabe erzeugt in PrimeNG gar kein Icon-Element.
   */
  protected resolveTertiaryIcon(item: LauncherModel): string {
    return item?.cssClasses?.trim() || item?.image?.trim() || '';
  }

  /**
   * Ob die Icon-Angabe eine Bild-URL ist (dann `<img>`) und keine Icon-Font-Klassen
   * (dann `<span>`). Mehrere Tokens sind immer eine Klassenliste -- eine URL
   * enthält keinen Whitespace.
   */
  protected isImageIcon(icon: string): boolean {
    if (!icon || /\s/.test(icon)) {
      return false;
    }
    return IMAGE_ICON_PATTERN.test(icon);
  }

  onClickTertiaryMenuItem(path: string) {
    if(this.debug) console.log('[NavigateMenuComponent], onClickTertiaryMenuItem, path=', path);
    if (!path) return;
    const isExternal = /^(http|https|mailto|tel):/i.test(path);
    if (isExternal) {
      if(this.debug) console.log('[NavigateMenuComponent], onClickTertiaryMenuItem, open in tab=', path);
      openUrl(path);
    } else {
      if(this.debug) console.log('[NavigateMenuComponent], onClickTertiaryMenuItem, open route=', path);
      this.router.navigate([path]);
    }
  }

  // Wird vom (setNotificationRead)-Output der <sx-notifications> ausgelöst. Die
  // gesamte Orchestrierung (optimistisches Overlay, Polling-Stop, Persistierung,
  // frischer Reload aus der DB, Rollback bei Fehler) liegt im NotificationsService.
  onSetNotificationRead(event: SetNotificationReadEvent): void {
    if (!event.userNotificationUuid) return;

    if(this.debug) console.log('[NaviagtionMenuComponent], onSetNotificationRead, event=', event);

    this.#notificationsService
      .markNotificationRead(event.userNotificationUuid, event.isRead)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        error: () =>
          this.#sxMessage.error(
            this.translate.instant('redesign.notifications.LOAD_ERROR'),
            this.translate.instant('redesign.toast.ERROR'),
          ),
      });
  }

}
